import type { SVGAttributes } from "react"

/**
 * AppLogoIcon — the canonical OKS lockup (the three-letter "OKS" mark
 * pulled from Figma node `350:801`).
 *
 * Renders three `<path>` elements (O / K / S) filled with `currentColor`
 * so consumers can recolor the mark via Tailwind text-color utilities:
 *   - Inside an orange tile (`<AppLogo>`): tile is `bg-sidebar-primary`
 *     and the mark is `text-white` → white "OKS" letterforms on orange.
 *   - Standalone on a light surface (`auth-simple-layout`): parent passes
 *     `text-foreground` → near-black "OKS" on the page background.
 *
 * Brand-avatar slots (invite-accept, invite-landing) MUST NOT render
 * this mark — the OKS lockup is OKS's identity, not the operating
 * brand's. Use `<BrandInitialMonogram>` instead (OKS-266 / OKS-275).
 *
 * The brand has NO separate square mark variant — the "OKS" lockup IS the
 * mark. Consumers SHOULD size by height (`h-N` Tailwind utility) and let
 * width auto-fit the ~3.13:1 aspect ratio. Width-only sizing (`size-N`,
 * `aspect-square`) will distort the letterforms and is forbidden.
 */
export default function AppLogoIcon(props: SVGAttributes<SVGElement>) {
  return (
    <svg
      viewBox="0 0 234.545 74.8746"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-hidden="true"
      {...props}
    >
      <g>
        {/* O */}
        <path
          fill="currentColor"
          d="M0 55.3626V19.51C0 15.0071 1.78073 10.6908 4.94929 7.50749L7.4728 4.97226C10.6414 1.78899 14.9378 0 19.4199 0H55.1068C59.5889 0 63.8853 1.78899 67.0539 4.97226L69.5774 7.50749C72.746 10.6908 74.5267 15.0091 74.5267 19.51V55.3626C74.5267 59.8655 72.746 64.1818 69.5774 67.3651L67.0539 69.9003C63.8853 73.0836 59.587 74.8726 55.1068 74.8726H19.4199C14.9378 74.8726 10.6414 73.0836 7.4728 69.9003L4.94929 67.3651C1.78073 64.1818 0 59.8655 0 55.3626Z"
        />
        {/* K */}
        <path
          fill="currentColor"
          d="M80.1616 71.2181V3.65457C80.1616 1.63582 81.7899 0 83.7993 0H113.786C115.796 0 117.424 1.63582 117.424 3.65457C117.424 6.9105 121.343 8.5424 123.634 6.23889L128.779 1.07025C129.461 0.384899 130.386 0 131.351 0H151.049C153.058 0 154.686 1.63582 154.686 3.65457V23.4435C154.686 24.4136 154.303 25.3424 153.621 26.0278L144.839 34.851C143.418 36.2787 143.418 38.592 144.839 40.0197L153.621 48.8429C154.303 49.5282 154.686 50.4571 154.686 51.4272V71.2161C154.686 73.2348 153.058 74.8707 151.049 74.8707H131.351C130.386 74.8707 129.461 74.4858 128.779 73.8004L123.634 68.6318C121.343 66.3302 117.424 67.9602 117.424 71.2161C117.424 73.2348 115.796 74.8707 113.786 74.8707H83.7993C81.7899 74.8707 80.1616 73.2348 80.1616 71.2161V71.2181Z"
        />
        {/* S */}
        <path
          fill="currentColor"
          d="M160.015 72.2137V52.576C160.015 51.1071 161.199 49.9151 162.663 49.9151H166.045C168.404 49.9151 169.585 47.0499 167.918 45.3748L160.793 38.2169C160.296 37.7181 160.017 37.0406 160.017 36.3356V13.5814C160.017 12.8764 160.296 12.1989 160.793 11.7001L171.663 0.779616C172.159 0.280819 172.834 0 173.535 0H231.897C233.359 0 234.545 1.19004 234.545 2.66091V22.2986C234.545 23.7675 233.361 24.9595 231.897 24.9595H228.515C226.156 24.9595 224.975 27.8246 226.643 29.4997L233.768 36.6577C234.264 37.1565 234.544 37.834 234.544 38.539V61.2931C234.544 61.9981 234.264 62.6756 233.768 63.1744L222.897 74.0949C222.401 74.5937 221.727 74.8746 221.025 74.8746H162.663C161.201 74.8746 160.015 73.6845 160.015 72.2137Z"
        />
      </g>
    </svg>
  )
}
